import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import html2canvas from 'html2canvas';
import { Navbar } from './Navbar';
import HeroCard from '../CardFiles/HeroCard';
import PersonalityCard from '../CardFiles/PersonalityCard';
import CharacterCard from '../CardFiles/CharacterCard';
import GrowthCard from '../CardFiles/GrowthCard';
import './CharacterCardGeneration.css';

const emojiMap = {
  chameleon: '🦎',
  default: '🌱'
};

// ─── Outlook Meter Component ──────────────────────────────────────
const OUTLOOK_STYLES = {
  'Optimist':     { color: '#34d399', bg: 'rgba(16, 185, 129, 0.12)', border: 'rgba(52, 211, 153, 0.25)', icon: '☀️' },
  'Realist':      { color: '#c084fc', bg: 'rgba(168, 85, 247, 0.12)', border: 'rgba(192, 132, 252, 0.25)', icon: '⚖️' },
  'Depth-Feeler': { color: '#60a5fa', bg: 'rgba(96, 165, 250, 0.12)', border: 'rgba(96, 165, 250, 0.25)', icon: '🌊' },
};

const OutlookMeter = ({ outlook }) => {
  // Support both new fields and old field names for backwards compatibility
  const outlookStyle = outlook?.outlookStyle || outlook?.outlookType;
  if (!outlook || !outlookStyle) return null;

  const score = outlook.outlookScore ?? outlook.optimismScore ?? 50;
  const markerPosition = Math.max(0, Math.min(100, score));
  const style = OUTLOOK_STYLES[outlookStyle] || OUTLOOK_STYLES['Realist'];

  // Support both new and old field names
  const description = outlook.description || outlook.reasoning;
  const tip = outlook.practicalTip;

  return (
    <div style={{ background: 'rgba(25, 20, 35, 0.5)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', padding: '24px 20px', marginBottom: '24px' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <h4 style={{ margin: 0, fontSize: '0.85rem', color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Outlook Style</h4>
        <div style={{ background: style.bg, color: style.color, padding: '4px 14px', borderRadius: '12px', fontSize: '0.8rem', fontWeight: 'bold', border: `1px solid ${style.border}` }}>
          {style.icon} {outlookStyle}
        </div>
      </div>

      {/* Tagline */}
      {outlook.tagline && (
        <p style={{ margin: '0 0 16px', fontSize: '0.95rem', color: style.color, fontWeight: 600, lineHeight: 1.4 }}>
          "{outlook.tagline}"
        </p>
      )}

      {/* The Meter */}
      <div style={{ position: 'relative', height: '8px', width: '100%', borderRadius: '4px', background: 'linear-gradient(90deg, #60a5fa 0%, #c084fc 50%, #34d399 100%)', marginBottom: '8px' }}>
        <div style={{ position: 'absolute', top: '-6px', bottom: '-6px', left: `${markerPosition}%`, width: '4px', background: '#fff', borderRadius: '2px', transform: 'translateX(-50%)', boxShadow: '0 0 8px rgba(255,255,255,0.8)' }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', marginBottom: '20px', letterSpacing: '0.05em' }}>
        <span>Depth-Feeler</span>
        <span>Realist</span>
        <span>Optimist</span>
      </div>

      {/* Description */}
      {description && (
        <div style={{ background: 'rgba(0,0,0,0.2)', padding: '16px', borderRadius: '12px', borderLeft: `3px solid ${style.color}`, marginBottom: '16px' }}>
          <p style={{ margin: 0, fontSize: '0.85rem', color: 'rgba(255,255,255,0.9)', lineHeight: 1.6, fontStyle: 'italic' }}>
            "{description}"
          </p>
        </div>
      )}

      {/* Secret Edge */}
      {outlook.secretEdge && (
        <div style={{ background: style.bg, border: `1px solid ${style.border}`, borderRadius: '12px', padding: '14px 16px', marginBottom: '16px' }}>
          <p style={{ margin: '0 0 6px', fontSize: '0.7rem', color: style.color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            ✦ Your Hidden Edge
          </p>
          <p style={{ margin: 0, fontSize: '0.85rem', color: 'rgba(255,255,255,0.88)', lineHeight: 1.55 }}>
            {outlook.secretEdge}
          </p>
        </div>
      )}

      {/* Practical Tip */}
      {tip && (
        <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px', padding: '14px 16px' }}>
          <p style={{ margin: '0 0 6px', fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            💡 {tip.title || 'Your Practical Tip'}
          </p>
          <p style={{ margin: 0, fontSize: '0.85rem', color: 'rgba(255,255,255,0.85)', lineHeight: 1.6 }}>
            {tip.tip}
          </p>
        </div>
      )}

    </div>
  );
};

// ─── Expandable Insight Row ───────────────────────────────────────
const ExpandRow = ({ icon, label, detail, watchOut }) => {
  const [open, setOpen] = useState(false);
  return (
    <div
      className={`expand-row ${open ? 'open' : ''}`}
      onClick={() => setOpen(o => !o)}
    >
      <div className="expand-row-header">
        <span className="expand-row-icon">{icon || '✦'}</span>
        <span className="expand-row-label">{label}</span>
        <span className="expand-row-chevron">{open ? '▲' : '▼'}</span>
      </div>
      {open && (
        <div className="expand-row-body">
          {detail && <p className="expand-detail">{detail}</p>}
          {watchOut && <p className="expand-watchout">⚠️ Watch out for: {watchOut}</p>}
        </div>
      )}
    </div>
  );
};

// ─── Social Battery Badge ─────────────────────────────────────────
const SOCIAL_BATTERY_CONFIG = {
  low:    {
    label: 'Low Battery',
    icon: '🔋',
    color: '#f87171',
    bg: 'rgba(248,113,113,0.12)',
    border: 'rgba(248,113,113,0.3)',
    tagline: 'Protecting your energy, not avoiding people.',
    desc: 'Solo time isn\'t a luxury — it\'s maintenance. You need quiet to recharge and think clearly.',
  },
  medium: {
    label: 'Half Charged',
    icon: '🔋',
    color: '#fbbf24',
    bg: 'rgba(251,191,36,0.12)',
    border: 'rgba(251,191,36,0.3)',
    tagline: 'People are great. In the right dose.',
    desc: 'You enjoy real connection but crash fast in overstimulating crowds. Quality over quantity.',
  },
  high: {
    label: 'Fully Charged',
    icon: '🔋',
    color: '#34d399',
    bg: 'rgba(52,211,153,0.12)',
    border: 'rgba(52,211,153,0.3)',
    tagline: 'People are fuel.',
    desc: 'You come alive in a crowd and go quiet without them. Isolation drains you faster than anything.',
  },
};

const SocialBatteryBadge = ({ level }) => {
  if (!level) return null;
  const key = level.toLowerCase();
  const cfg = SOCIAL_BATTERY_CONFIG[key] || SOCIAL_BATTERY_CONFIG.medium;
  const fillPct = key === 'low' ? 18 : key === 'medium' ? 52 : 92;
  const segments = [
    { pct: 15,  active: fillPct >= 15 },
    { pct: 40,  active: fillPct >= 40 },
    { pct: 65,  active: fillPct >= 65 },
    { pct: 90,  active: fillPct >= 90 },
  ];

  return (
    <div style={{ background: 'rgba(25,20,35,0.5)', border: `1px solid ${cfg.border}`, borderRadius: '16px', padding: '16px 20px', marginBottom: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
        <h4 style={{ margin: 0, fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Social Battery</h4>
        <div style={{ background: cfg.bg, color: cfg.color, padding: '3px 12px', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 800, border: `1px solid ${cfg.border}`, letterSpacing: '0.02em' }}>
          {cfg.icon} {cfg.label}
        </div>
      </div>
      <p style={{ margin: '0 0 12px', fontSize: '0.95rem', fontWeight: 700, color: 'rgba(255,255,255,0.9)', lineHeight: 1.3 }}>{cfg.tagline}</p>
      {/* Segmented battery bar */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '10px' }}>
        {segments.map((s, i) => (
          <div key={i} style={{
            flex: 1, height: '6px', borderRadius: '3px',
            background: s.active ? cfg.color : 'rgba(255,255,255,0.08)',
            transition: `background 0.4s ease ${i * 0.1}s`,
          }} />
        ))}
      </div>
      <p style={{ margin: 0, fontSize: '0.82rem', color: 'rgba(255,255,255,0.5)', lineHeight: 1.55 }}>{cfg.desc}</p>
    </div>
  );
};


// ─── 3-Chapter Scroll ────────────────────────────────────────────
const ChapterHeader = ({ num, icon, title, subtitle }) => (
  <div className="chapter-header">
    <div className="chapter-icon-wrapper">
      <div className="chapter-num">{num}</div>
      <div className="chapter-icon">{icon}</div>
    </div>
    <div className="chapter-title-group">
      <h3 className="chapter-title">{title}</h3>
      <p className="chapter-subtitle">{subtitle}</p>
    </div>
  </div>
);

const ChapterScroll = ({
  traits, strengths, charTraits,
  careers, roles, careerRationale,
  growthTips, parentingDo, parentingDont,
  selfImprovementDaily, selfImprovementWeekly, motivation, defaultAge,
  generalOutlook, crisisMode, socialBattery
}) => {
  const isChild = defaultAge && Number(defaultAge) < 18;
  const [guideView, setGuideView] = useState(isChild ? 'child' : 'me');

  const guideOptions = [
    { key: 'me', label: 'For Me' },
    { key: 'child', label: 'For My Child' }
  ].filter(opt => {
    if (isChild && opt.key === 'me') return false;
    if (!isChild && opt.key === 'child') return false;
    return true;
  });

  const activeView = guideOptions.length === 1 ? guideOptions[0].key : guideView;

  // Merge the three trait sources into one list, capped at 5 to avoid wall-of-text
  const allTraits = [
    ...traits.slice(0, 3).map(t => ({ icon: '✦', label: t.label, detail: t.detail, watchOut: t.watchOut })),
    ...strengths.slice(0, 2).map(s => ({ icon: s.icon || emojiMap.default, label: s.label, detail: s.detail, watchOut: '' })),
  ].slice(0, 5);

  return (
    <div className="chapter-scroll-container">

      {/* ── Chapter 1: Your Edge ── */}
      <div className="chapter-section" id="chapter-1">
        <ChapterHeader
          num="01"
          icon="🚀"
          title="Your Edge"
          subtitle="Competitive Advantage & Potential"
        />

        {careerRationale && (
          <p style={{
            fontSize: '0.88rem', color: 'rgba(255,255,255,0.65)', fontStyle: 'italic',
            lineHeight: 1.6, margin: '0 0 14px', paddingLeft: '12px',
            borderLeft: '2px solid rgba(168,85,247,0.5)',
          }}>{careerRationale}</p>
        )}

        {careers.length > 0 && (
          <>
            <p className="chapter-sublabel">Ambition Alignment</p>
            <div className="edge-pills">
              {careers.map((c, i) => <span key={i} className="edge-pill">{c}</span>)}
            </div>
          </>
        )}

        {roles.length > 0 && (
          <>
            <p className="chapter-sublabel" style={{ marginTop: '0.75rem' }}>Ideal Roles</p>
            <div className="edge-pills">
              {roles.slice(0, 4).map((r, i) => <span key={i} className="edge-pill role">{r}</span>)}
            </div>
          </>
        )}

        {motivation && (
          <div className="motivation-card">
            <span className="motivation-icon">✨</span>
            <p className="motivation-text">{motivation.slice(0, 220)}</p>
          </div>
        )}
      </div>

      {/* ── Chapter 2: Who You Are ── */}
      <div className="chapter-section" id="chapter-2">
        <ChapterHeader
          num="02"
          icon="🧭"
          title="Who You Are"
          subtitle="Identity & Natural Gifts"
        />

        {/* Outlook Meter */}
        {generalOutlook && <OutlookMeter outlook={generalOutlook} />}

        {/* Social Battery */}
        {socialBattery && <SocialBatteryBadge level={socialBattery} />}



        {allTraits.length > 0 && (
          <>
            <p className="chapter-sublabel">Your Defining Traits</p>
            {allTraits.map((t, i) => (
              <ExpandRow key={i} icon={t.icon} label={t.label} detail={t.detail} watchOut={t.watchOut} />
            ))}
          </>
        )}
      </div>

      {/* ── Crisis Mode ── */}
      {crisisMode && (
        <div className="chapter-section" id="crisis-mode" style={{
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 60%, #0f3460 100%)',
          borderRadius: '20px',
          padding: '0',
          overflow: 'hidden',
          margin: '0',
        }}>
          {/* Header */}
          <div style={{ padding: '20px 20px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '1.1rem' }}>🔥</span>
              <span style={{ fontSize: '0.7rem', fontWeight: 900, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)' }}>
                You Under Pressure
              </span>
            </div>
            {crisisMode.label && (
              <span style={{
                fontSize: '0.7rem', fontWeight: 900, padding: '3px 10px',
                borderRadius: '999px', background: 'rgba(239,68,68,0.18)',
                color: '#fca5a5', border: '1px solid rgba(239,68,68,0.3)',
              }}>
                {crisisMode.label}
              </span>
            )}
          </div>

          <div style={{ padding: '0 16px 20px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {crisisMode.trigger && (
              <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px', padding: '12px 14px' }}>
                <div style={{ fontSize: '0.6rem', fontWeight: 900, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(252,165,165,0.75)', marginBottom: '5px' }}>
                  Triggered by
                </div>
                <p style={{ margin: 0, fontSize: '0.88rem', color: 'rgba(255,255,255,0.82)', lineHeight: 1.55 }}>{crisisMode.trigger}</p>
              </div>
            )}
            {crisisMode.howTheyLook && (
              <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px', padding: '12px 14px' }}>
                <div style={{ fontSize: '0.6rem', fontWeight: 900, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(253,186,116,0.75)', marginBottom: '5px' }}>
                  What others see
                </div>
                <p style={{ margin: 0, fontSize: '0.88rem', color: 'rgba(255,255,255,0.82)', lineHeight: 1.55 }}>{crisisMode.howTheyLook}</p>
              </div>
            )}
            {crisisMode.whatHelps && (
              <div style={{ background: 'rgba(134,239,172,0.07)', border: '1px solid rgba(134,239,172,0.2)', borderRadius: '12px', padding: '12px 14px' }}>
                <div style={{ fontSize: '0.6rem', fontWeight: 900, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(134,239,172,0.85)', marginBottom: '5px' }}>
                  What actually helps
                </div>
                <p style={{ margin: 0, fontSize: '0.88rem', color: 'rgba(255,255,255,0.82)', lineHeight: 1.55 }}>{crisisMode.whatHelps}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Chapter 3: The Growth Path (Adults only) ── */}
      {!isChild && (
        <div className="chapter-section" id="chapter-3">
          <ChapterHeader
            num="03"
            icon="💬"
            title="The Growth Path"
            subtitle="Personal Evolution"
          />

          <div className="guide-content">
            {growthTips.length > 0 && (
              <>
                <p className="chapter-sublabel">Areas to Work On</p>
                {growthTips.map((g, i) => {
                  const label = typeof g === 'object' ? g.area || g.label || 'Focus Area' : String(g);
                  const timeline = typeof g === 'object' ? g.timeline : '';
                  return (
                    <div key={`growth-${i}`} className="guide-action-row">
                      <span className="guide-action-num">{i + 1}</span>
                      <span className="guide-action-text">{label}{timeline ? ` · ${timeline}` : ''}</span>
                    </div>
                  );
                })}
              </>
            )}
            {selfImprovementDaily?.length > 0 && (
              <>
                <p className="chapter-sublabel" style={{ marginTop: growthTips.length > 0 ? '1rem' : 0 }}>Daily Momentum</p>
                {selfImprovementDaily.map((habit, i) => (
                  <div key={`daily-${i}`} className="guide-action-row">
                    <span className="guide-action-num">{i + 1}</span>
                    <span className="guide-action-text">{habit}</span>
                  </div>
                ))}
              </>
            )}
            {selfImprovementWeekly?.length > 0 && (
              <>
                <p className="chapter-sublabel" style={{ marginTop: '1rem' }}>Weekly Goals</p>
                {selfImprovementWeekly.map((goal, i) => (
                  <div key={`weekly-${i}`} className="guide-action-row">
                    <span className="guide-action-num">{i + 1}</span>
                    <span className="guide-action-text">{goal}</span>
                  </div>
                ))}
              </>
            )}
            {(!growthTips?.length && !selfImprovementDaily?.length && !selfImprovementWeekly?.length) && (
              <p className="guide-empty">Complete your profile to unlock custom evolution steps.</p>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export const CharacterCardGeneration = ({
  user,
  analysisResult,
  onSignInClick,
  onBack,
  onComplete,
  onGoHome,
  onGoToConnections
}) => {
  const [cardGenerationStage, setCardGenerationStage] = useState('ready');
  const [cardsData, setCardsData] = useState([]);
  const [comparisonNotes, setComparisonNotes] = useState(null);
  const [error, setError] = useState('');
  const [generationProgress, setGenerationProgress] = useState(0);
  const [inviteLink, setInviteLink] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [captionCopied, setCaptionCopied] = useState(false);
  const cardRef = useRef(null);
  const heroRef = useRef(null);

  // Fetch or create the user's invite link (same as Connections tab)
  useEffect(() => {
    if (!user?.id) return;
    const fetchInviteLink = async () => {
      try {
        // Try to reuse existing invite
        const activeRes = await fetch(`/api/invites/active/${user.id}`);
        const activeData = await activeRes.json();
        if (activeData.success && activeData.invite_link) {
          setInviteLink(activeData.invite_link);
          return;
        }
        // Create new invite
        const res = await fetch('/api/invites/create', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ inviter_user_id: user.id, inviter_test_results: analysisResult || {} })
        });
        const data = await res.json();
        if (data.success) setInviteLink(data.invite_link);
      } catch (e) { console.error('Failed to fetch invite link:', e); }
    };
    fetchInviteLink();
  }, [user?.id, analysisResult]);

  // Derived state for view restriction
  // View is restricted ONLY if it's a shared view AND the current user is NOT the owner
  const isOwner = user?.id && analysisResult?.ownerId && String(user.id) === String(analysisResult.ownerId);
  const isRestrictedView = analysisResult?.isSharedView && !isOwner;

  // Sync address bar to the invite link once available and card generation is complete
  useEffect(() => {
    if (inviteLink && cardGenerationStage === 'complete' && !isRestrictedView) {
      try {
        const urlObj = new URL(inviteLink);
        const relativeUrl = urlObj.pathname + urlObj.search;
        window.history.replaceState({ path: relativeUrl }, '', relativeUrl);
      } catch (e) {
        window.history.replaceState({ path: inviteLink }, '', inviteLink);
      }
    }
  }, [inviteLink, cardGenerationStage, isRestrictedView]);

  // Log comparison notes when available
  useEffect(() => {
    if (comparisonNotes) {
      console.log('Comparison Notes:', comparisonNotes);
    }
  }, [comparisonNotes]);

  // Share Logic Helper
  const handleShareClick = () => {
    const shareUrl = inviteLink || `${window.location.origin}?share=${analysisResult?.card_id || ''}&ref=${user?.id || ''}`;
    if (shareUrl) {
      if (navigator.share) {
        navigator.share({
          title: 'My Personality Card',
          text: 'Check out my personality analysis!',
          url: shareUrl
        }).catch(console.error);
      } else {
        // Fallback to clipboard
        navigator.clipboard.writeText(shareUrl).then(() => {
          alert('Share link copied!');
        });
      }
    } else {
      alert('Please save the card first to get a link.');
    }
  };

  useEffect(() => {
    // Check for either nested cards_data OR flat structure (from shared view)
    const hasData = analysisResult?.cards_data || analysisResult?.personality_card;

    if (hasData && cardsData.length === 0) {
      console.log('Auto-loading cards from analysisResult');

      // Handle both naming conventions and potential string-encoded JSON
      let source = analysisResult?.card_data || analysisResult?.cards_data || analysisResult;

      if (typeof source === 'string') {
        try { source = JSON.parse(source); } catch (e) { }
      }

      // Check if source itself is the card container OR if cards are inside a 'cards_data' key
      const personality_card = source.personality_card || source.personality || source;
      const character_card = source.character_card || source.character || source;
      const growth_card = source.growth_card || source.growth || source;

      let formattedCards = [
        { type: 'personality', content: personality_card },
        { type: 'character', content: character_card },
        { type: 'growth', content: growth_card }
      ];

      // RESTRICT SHARED VIEW: Only show the first card (Personality)
      if (isRestrictedView) {
        formattedCards = [formattedCards[0]];
      }

      // Update state if data is present
      if (formattedCards[0]?.content && cardsData.length === 0) {
        setCardsData(formattedCards);
        setCardGenerationStage('complete');
        setGenerationProgress(100);
      }

      // Robust extraction for comparison_notes
      const cNotes = analysisResult.comparison_notes || analysisResult.card_metadata || source.comparison_notes;
      if (cNotes) {
        setComparisonNotes(cNotes);
      }
    } else if (!hasData && cardGenerationStage === 'ready' && analysisResult && user) {
      // Auto-trigger generation if we have analysisResult but no generated cards yet
      handleGenerateCard();
    }
  }, [analysisResult, cardsData.length, isRestrictedView, user, cardGenerationStage]);


  const handleGenerateCard = async () => {
    try {
      setCardGenerationStage('generating');
      setGenerationProgress(0);
      setError('');
      // Check if cards were already generated during analysis (single-shot mode)
      const pregenCards = analysisResult?.cards_data;
      const personalityCard = pregenCards?.personality_card;
      // Only accept pre-generated cards if personality_card has real content (name + at least one key field)
      if (pregenCards && personalityCard?.name && (personalityCard?.oneLiner || personalityCard?.persona)) {
        console.log('Using pre-generated cards from analysis');
        const formattedCards = [
          { type: 'personality', content: personalityCard },
          { type: 'character', content: pregenCards.character_card },
          { type: 'growth', content: pregenCards.growth_card }
        ];
        setCardsData(formattedCards);
        setCardGenerationStage('complete');
        setGenerationProgress(100);
        return;
      }
      if (pregenCards && !personalityCard?.name) {
        console.warn('Pre-generated cards_data present but personality_card is empty — falling back to API');
      }

      // Fallback: Make API call if cards not pre-generated (legacy/error case)
      console.log('Cards not pre-generated, fetching from API...');

      // Simulate progress
      const progressInterval = setInterval(() => {
        setGenerationProgress(prev => {
          if (prev >= 95) return 95;
          return prev + Math.floor(Math.random() * 5) + 1;
        });
      }, 300);

      // Call API to generate strict JSON data
      // We send analysisResult which contains user_profile and brain_dominance_analysis
      const response = await fetch('/api/prompts/analyze/card', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          analysis_results: analysisResult,
          user_id: user?.id,
          analysis_id: analysisResult?.analysis_id,
          provider: 'openai'
        }),
      });

      const data = await response.json();

      clearInterval(progressInterval);
      setGenerationProgress(100);

      const pCard = data.cards_data?.personality_card;
      if (data.cards_data && pCard?.name && (pCard?.oneLiner || pCard?.persona)) {
        // Transform the structured response into an array for the carousel
        const formattedCards = [
          { type: 'personality', content: pCard },
          { type: 'character', content: data.cards_data.character_card },
          { type: 'growth', content: data.cards_data.growth_card }
        ];

        setCardsData(formattedCards);
        setComparisonNotes(data.comparison_notes);
        setCardGenerationStage('complete');

        // Add card_id to analysisResult for sharing
        if (data.card_id) {
          analysisResult.card_id = data.card_id;
        }

        // We do NOT call onComplete(data) here because we want the user
        // to stay on this screen to read, share, and download their cards.
      } else {
        throw new Error(data.error || 'Failed to generate card data');
      }

    } catch (err) {
      console.error('Card generation error:', err);
      setError(err.message || 'Something went wrong while generating cards.');
      setCardGenerationStage('error');
    }
  };

  const handleDownload = async () => {
    // Prioritize Hero Card for download/share
    const target = heroRef.current || cardRef.current;
    if (!target) return;

    try {
      const personalityContent = cardsData.find(c => c.type === 'personality')?.content;
      const filename = `${(personalityContent?.name || 'MyCard').replace(/\s+/g, '_')}_HeroCard.png`;

      const canvas = await html2canvas(target, {
        scale: 2,
        backgroundColor: '#1a1a2e',
        logging: false,
        useCORS: true
      });

      const link = document.createElement('a');
      link.download = filename;
      link.href = canvas.toDataURL('image/png');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Download failed:', err);
      alert('Failed to download card image');
    }
  };

  // Check for either nested cards_data OR flat structure (from shared view)
  const hasCardData = analysisResult?.cards_data || analysisResult?.personality_card;

  if (!user && cardGenerationStage === 'ready' && !hasCardData) {
    return (
      <div className="min-h-screen bg-gray-50 bg-pattern">
        <Navbar user={user} onSignInClick={onSignInClick} />
        <div className="max-w-4xl mx-auto px-4 py-12 flex flex-col items-center justify-center text-center">
          <div className="bg-white p-8 rounded-2xl shadow-xl max-w-lg w-full">
            <h2 className="text-2xl font-bold mb-4 text-gray-800">Sign in to Create Cards</h2>
            <p className="text-gray-600 mb-8">
              Create beautiful, personalized personality cards to share with friends.
              Your analysis is ready to be transformed into valid Character Cards.
            </p>
            <div className="flex gap-4 justify-center">
              <button onClick={onBack} className="px-6 py-3 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors">
                Back
              </button>
              <button onClick={onSignInClick} className="px-6 py-3 rounded-xl bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">
                Sign In to Continue
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" style={{
      background: 'linear-gradient(180deg, #2E003E 0%, #0F1020 100%)',
      color: 'white'
    }}>
      <Navbar user={user} onSignInClick={onSignInClick} onGoHome={onGoHome} />

      <div className="flex-1 flex flex-col container mx-auto px-4 pt-20 pb-8 max-w-6xl">

        {/* Header - only for shared views */}
        {isRestrictedView ? (
          <div className="mb-6 flex justify-between items-center">
            <div className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">
              {analysisResult?.ownerName ? `${analysisResult.ownerName}'s Analysis` : 'Shared Analysis'}
            </div>
          </div>
        ) : (
          <div className="mb-4" />
        )}

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col items-center justify-center min-h-[600px]">

          {/* STAGE: READY / ERROR FALLBACK */}
          {cardGenerationStage === 'ready' && (
            <div className="text-center max-w-xl mx-auto animate-fade-in">
              <div className="mb-8 relative">
                <div className="w-24 h-24 bg-gradient-to-tr from-red-500 to-orange-600 rounded-2xl mx-auto shadow-2xl flex items-center justify-center rotate-3 hover:rotate-6 transition-transform duration-300">
                  <span className="text-4xl">⚠️</span>
                </div>
                <div className="absolute -z-10 top-0 left-1/2 -translate-x-1/2 w-32 h-32 bg-red-500/20 rounded-full blur-2xl"></div>
              </div>

              <h2 className="text-3xl font-bold mb-4 text-gray-900">
                Analysis Incomplete
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                The Master Graphologist analyzed your handwriting, but the neural output was abruptly truncated before your cards could be fully painted.
                <br />
                <span className="font-semibold text-gray-800">Please return to the upload screen and try again.</span>
              </p>

              <button
                onClick={() => onBack && onBack()}
                className="group relative inline-flex items-center justify-center px-8 py-4 text-lg font-bold text-white transition-all duration-200 bg-gray-900 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 hover:bg-gray-800 hover:scale-105 active:scale-95 shadow-xl"
              >
                <span className="mr-2">🔄</span>
                Restart Analysis
              </button>
            </div>
          )}

          {/* STAGE: GENERATING */}
          {cardGenerationStage === 'generating' && (
            <div className="flex flex-col items-center justify-center w-full max-w-md mx-auto">
              <div className="relative w-24 h-24 mb-8">
                <div className="absolute inset-0 border-4 border-gray-100 rounded-full"></div>
                <div
                  className="absolute inset-0 border-4 border-blue-600 rounded-full border-t-transparent animate-spin"
                ></div>
                <div className="absolute inset-0 flex items-center justify-center font-bold text-gray-700">
                  {generationProgress}%
                </div>
              </div>

              <h3 className="text-xl font-bold text-gray-900 mb-2">Analyzing Personality Data...</h3>
              <p className="text-gray-500 text-center animate-pulse">
                Selecting correct themes and styling for your unique profile...
              </p>
            </div>
          )}

          {/* STAGE: COMPLETE — Hero Card + Tabbed Deep Dive */}
          {cardGenerationStage === 'complete' && cardsData.length > 0 && (
            <div className="w-full max-w-6xl flex flex-col items-center animate-fade-in-up">

              {/* ─── Back to Dashboard ─── */}
              {onGoHome && !isRestrictedView && (
                <div className="w-full max-w-md mb-4 flex justify-start">
                  <button
                    onClick={onGoHome}
                    className="flex items-center gap-2 px-5 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full text-sm font-semibold transition-all shadow-lg backdrop-blur-md text-white/70 hover:text-white group"
                  >
                    <svg className="w-4 h-4 transition-transform group-hover:-translate-x-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="19" y1="12" x2="5" y2="12"></line>
                      <polyline points="12 19 5 12 12 5"></polyline>
                    </svg>
                    Dashboard
                  </button>
                </div>
              )}

              {/* ─── Hero Card (The Shareable Viral Unit) ─── */}
              <div ref={heroRef} className="w-full max-w-md mb-6 relative">
                <HeroCard
                  personalityData={cardsData.find(c => c.type === 'personality')?.content}
                  growthData={cardsData.find(c => c.type === 'growth')?.content}
                  onShare={() => {
                    const shareUrl = inviteLink || `${window.location.origin}?share=${analysisResult?.card_id || ''}&ref=${user?.id || ''}`;
                    if (navigator.share) {
                      navigator.share({ title: 'My Brain Vibe', url: shareUrl }).catch(() => { });
                    } else {
                      navigator.clipboard?.writeText(shareUrl);
                      if (typeof handleShareClick === 'function') handleShareClick();
                    }
                  }}
                />
              </div>

              {/* ─── Share Caption (copy-ready for Stories/TikTok) ─── */}
              {(() => {
                const pData = cardsData.find(c => c.type === 'personality')?.content || {};
                const caption = pData.shareCaption;
                if (!caption || isRestrictedView) return null;
                return (
                  <div style={{
                    width: '100%', maxWidth: '28rem', marginBottom: '20px',
                    position: 'relative', borderRadius: '20px', overflow: 'hidden',
                    background: 'linear-gradient(135deg, rgba(139,92,246,0.15) 0%, rgba(59,130,246,0.15) 100%)',
                    border: '1.5px solid rgba(139,92,246,0.35)',
                    boxShadow: '0 0 24px rgba(139,92,246,0.12)',
                  }}>
                    {/* top accent bar */}
                    <div style={{
                      height: '3px',
                      background: 'linear-gradient(90deg, #8b5cf6, #3b82f6, #06b6d4)',
                    }} />
                    <div style={{ padding: '14px 16px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                        <span style={{ fontSize: '0.75rem' }}>✨</span>
                        <p style={{ margin: 0, fontSize: '0.65rem', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 800 }}>
                          Drop this on your story
                        </p>
                      </div>
                      <p style={{
                        margin: '0 0 14px', fontSize: '0.92rem', fontWeight: 600, lineHeight: 1.5,
                        color: 'rgba(255,255,255,0.95)', fontStyle: 'italic',
                        borderLeft: '3px solid rgba(139,92,246,0.6)', paddingLeft: '12px',
                      }}>
                        "{caption}"
                      </p>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => {
                            navigator.clipboard?.writeText(caption).then(() => {
                              setCaptionCopied(true);
                              setTimeout(() => setCaptionCopied(false), 2500);
                            });
                          }}
                          style={{
                            flex: 1,
                            background: captionCopied
                              ? 'linear-gradient(135deg, #10b981, #059669)'
                              : 'linear-gradient(135deg, #8b5cf6, #3b82f6)',
                            border: 'none', borderRadius: '10px',
                            padding: '9px 0', color: '#fff',
                            fontSize: '0.8rem', fontWeight: 800, cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            letterSpacing: '0.04em',
                          }}
                        >
                          {captionCopied ? '✓ Copied!' : '📋 Copy Caption'}
                        </button>
                        <button
                          onClick={() => {
                            const text = `${caption}\n\nFind your Brain Vibe → brainvibe.app`;
                            if (navigator.share) {
                              navigator.share({ text }).catch(() => {});
                            } else {
                              navigator.clipboard?.writeText(text);
                            }
                          }}
                          style={{
                            background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.15)',
                            borderRadius: '10px', padding: '9px 14px', color: 'rgba(255,255,255,0.7)',
                            fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer',
                          }}
                        >
                          Share ↗
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Restricted view fallback CTA */}
              {isRestrictedView && (
                <div className="mb-8 text-center animate-fade-in-up delay-300 w-full px-4">
                  <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-[2px] rounded-2xl mx-auto max-w-md">
                    <div className="bg-white rounded-2xl p-6 shadow-xl">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Want to know your own archetype?</h3>
                      <p className="text-gray-600 mb-6 text-sm">Discover your unique personality traits, strengths, and growth path.</p>
                      <button
                        onClick={() => {
                          const urlParams = new URLSearchParams(window.location.search);
                          const ref = urlParams.get('ref');
                          if (ref) {
                            sessionStorage.setItem('referrerId', ref);
                          }
                          window.location.href = '/';
                        }}
                        className="w-full py-4 bg-gray-900 text-white rounded-xl font-bold text-lg hover:bg-gray-800 transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                      >
                        Take the Test Free 🚀
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ─── 3 Life Changers (Viral Section) ─── */}
              {!isRestrictedView && (() => {
                const pData = cardsData.find(c => c.type === 'personality')?.content || {};
                const lifeChangers = pData.lifeChangers || [];
                if (lifeChangers.length === 0) return null;

                return (
                  <div className="w-full max-w-md mx-auto mt-2 mb-2">
                    <div className="life-changers-section">
                      <div className="life-changers-header">
                        <span className="life-changers-icon">🔑</span>
                        <h3 className="life-changers-title">3 Things That Will Change Your Life</h3>
                        <p className="life-changers-subtitle">Personalized for you — based on who you truly are</p>
                      </div>
                      {lifeChangers.slice(0, 3).map((item, i) => (
                        <div key={i} className="life-changer-card">
                          <div className="life-changer-num">{i + 1}</div>
                          <div className="life-changer-content">
                            <h4 className="life-changer-title">{item.title}</h4>
                            <p className="life-changer-why">{item.why}</p>
                            <div className="life-changer-action">
                              <span className="life-changer-action-label">⚡ Do this today:</span>
                              <span className="life-changer-action-text">{item.action}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* ─── 3 Things You Already Do Right ─── */}
              {!isRestrictedView && (() => {
                const pData = cardsData.find(c => c.type === 'personality')?.content || {};
                const keepDoing = pData.keepDoing || [];
                if (keepDoing.length === 0) return null;

                return (
                  <div className="w-full max-w-md mx-auto mb-2">
                    <div className="keep-doing-section">
                      <div className="keep-doing-header">
                        <span className="keep-doing-icon">✅</span>
                        <h3 className="keep-doing-title">3 Things You Already Do Right</h3>
                        <p className="keep-doing-subtitle">Don't change these — they make you, you</p>
                      </div>
                      {keepDoing.slice(0, 3).map((item, i) => (
                        <div key={i} className="keep-doing-card">
                          <div className="keep-doing-num">{i + 1}</div>
                          <div className="keep-doing-content">
                            <h4 className="keep-doing-card-title">{item.title}</h4>
                            <p className="keep-doing-why">{item.why}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* ─── Parenting Tips (Kids Only — shown early) ─── */}
              {!isRestrictedView && (() => {
                const gData = cardsData.find(c => c.type === 'growth')?.content || {};
                const pData = cardsData.find(c => c.type === 'personality')?.content || {};
                const age = gData.age || pData.age || analysisResult?.age;
                if (!age || Number(age) >= 18) return null;

                const parentingAdvice = gData.parentingAdvice || {};
                const doMore = parentingAdvice.doMoreOf || parentingAdvice.doMore || parentingAdvice.actionSteps || [];
                const doLess = parentingAdvice.doLessOf || parentingAdvice.avoidDoing || parentingAdvice.doLess || [];
                if (doMore.length === 0 && doLess.length === 0) return null;

                return (
                  <div className="w-full max-w-md mx-auto mb-2">
                    <div className="parenting-tips-section">
                      <div className="parenting-tips-header">
                        <span className="parenting-tips-icon">💚</span>
                        <h3 className="parenting-tips-title">
                          {Number(age) >= 13 ? 'Tips for Parents & Mentors' : 'Tips for Parents'}
                        </h3>
                        <p className="parenting-tips-subtitle">Help them grow into their best self</p>
                      </div>
                      {doMore.length > 0 && (
                        <div style={{ marginBottom: doLess.length > 0 ? '0.75rem' : 0 }}>
                          <p className="parenting-tips-sublabel">✓ Do More Of</p>
                          {doMore.slice(0, 3).map((tip, i) => (
                            <div key={i} className="parenting-tip-row do-more">
                              <span className="parenting-tip-icon">✓</span>
                              <span className="parenting-tip-text">{tip}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {doLess.length > 0 && (
                        <div>
                          <p className="parenting-tips-sublabel">✗ Avoid Doing</p>
                          {doLess.slice(0, 3).map((tip, i) => (
                            <div key={i} className="parenting-tip-row do-less">
                              <span className="parenting-tip-icon">✗</span>
                              <span className="parenting-tip-text">{tip}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* ─── 3-Chapter Scroll ─── */}
              {!isRestrictedView && (() => {
                const pData = cardsData.find(c => c.type === 'personality')?.content || {};
                const cData = cardsData.find(c => c.type === 'character')?.content || {};
                const gData = cardsData.find(c => c.type === 'growth')?.content || {};

                // Normalize traits & strengths
                const traits = Array.isArray(pData.keyInsights)
                  ? pData.keyInsights.map((t, i) => typeof t === 'object'
                    ? { label: t.insight || t.label || t.name || `Insight ${i + 1}`, detail: t.description || t.detail || '', watchOut: t.watchOut || '' }
                    : { label: `Insight ${i + 1}`, detail: String(t), watchOut: '' })
                  : [];
                const strengths = Array.isArray(pData.coreStrengths)
                  ? pData.coreStrengths.map(s => typeof s === 'object' ? { label: s.label || s.strength || s.name, icon: s.icon || '⚡', detail: s.description || s.detail || '', watchOut: '' } : { label: String(s), icon: '⚡', detail: '', watchOut: '' })
                  : [];
                const charTraits = Array.isArray(cData.characterTraits)
                  ? cData.characterTraits.map(t => typeof t === 'object' ? { label: t.trait || t.name || t.label, icon: t.icon || '🔮', detail: t.manifestation || t.description || t.detail || '' } : { label: String(t), icon: '🔮', detail: '' })
                  : [];
                const careers = gData.careerAlignment?.bestFitAreas || [];
                const roles = gData.careerAlignment?.specificRoles || [];
                const careerRationale = gData.careerAlignment?.careerRationale || '';
                const growthTips = (gData.growthOpportunities || []).slice(0, 4);
                // Guide Section - Robust Extraction
                const parentingAdvice = gData.parentingAdvice || {};
                const parentingDo = parentingAdvice.doMoreOf || parentingAdvice.doMore || parentingAdvice.actionSteps || [];
                const parentingDont = parentingAdvice.doLessOf || parentingAdvice.avoidDoing || parentingAdvice.doLess || [];

                const selfImprovementPlan = gData.selfImprovementPlan || {};
                const selfImprovementDaily = selfImprovementPlan.dailyHabits || selfImprovementPlan.daily || [];
                const selfImprovementWeekly = selfImprovementPlan.weeklyGoals || selfImprovementPlan.weekly || [];
                const motivation = gData.motivation?.personalizedMessage || '';

                return (
                  <div className="w-full max-w-md mx-auto mt-2">
                    <ChapterScroll
                      traits={traits}
                      strengths={strengths}
                      charTraits={charTraits}
                      careers={careers}
                      roles={roles}
                      careerRationale={careerRationale}
                      growthTips={growthTips}
                      parentingDo={parentingDo}
                      parentingDont={parentingDont}
                      selfImprovementDaily={selfImprovementDaily}
                      selfImprovementWeekly={selfImprovementWeekly}
                      motivation={motivation}
                      defaultAge={analysisResult?.age || pData?.age || user?.age}
                      generalOutlook={pData.generalOutlook}
                      crisisMode={cData.crisisMode}
                      socialBattery={cData.lifeBalance?.socialBattery}
                    />
                  </div>
                );
              })()}

              {/* ─── Connection CTA Card ─── */}
              {!isRestrictedView && (
                <div className="w-full max-w-md mx-auto mt-4 mb-12">
                  <div className="connection-cta-card">
                    {/* Header */}
                    <div className="connection-cta-header">
                      <span className="connection-cta-emoji">🔮</span>
                      <h3 className="connection-cta-title">See How You Match With Anyone</h3>
                      <p className="connection-cta-subtitle">
                        Send your link to a friend, partner, or family member. Once they take the test, you'll unlock:
                      </p>
                    </div>

                    {/* Preview of what you unlock */}
                    <div className="connection-preview-grid">
                      <div className="connection-preview-item">
                        <span className="connection-preview-icon">💯</span>
                        <span className="connection-preview-label">Compatibility Score</span>
                      </div>
                      <div className="connection-preview-item">
                        <span className="connection-preview-icon">🤝</span>
                        <span className="connection-preview-label">How You Click</span>
                      </div>
                      <div className="connection-preview-item">
                        <span className="connection-preview-icon">⚡</span>
                        <span className="connection-preview-label">Friction Points</span>
                      </div>
                      <div className="connection-preview-item">
                        <span className="connection-preview-icon">💡</span>
                        <span className="connection-preview-label">Tips to Get Along</span>
                      </div>
                    </div>

                    {/* Share actions using the real invite link */}
                    {inviteLink ? (
                      <div className="connection-cta-actions">
                        <button
                          className="connection-btn whatsapp"
                          onClick={() => {
                            const persona = cardsData.find(c => c.type === 'personality')?.content?.persona || 'unique';
                            const text = `I just found out I'm "${persona}" ✨ Take this personality test and let's see if we're better as friends, lovers, or startup partners: ${inviteLink}`;
                            window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
                          }}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" /></svg>
                          WhatsApp
                        </button>
                        <button
                          className="connection-btn sms"
                          onClick={() => {
                            const persona = cardsData.find(c => c.type === 'personality')?.content?.persona || 'unique';
                            const text = `This handwriting test nailed my personality 🔥 I'm "${persona}" — take it and let's find out if we'd survive a road trip, a startup, or a relationship: ${inviteLink}`;
                            window.open(`sms:?body=${encodeURIComponent(text)}`, '_blank');
                          }}
                        >
                          💬 SMS
                        </button>
                        <button
                          className={`connection-btn copy ${linkCopied ? 'copied' : ''}`}
                          onClick={() => {
                            navigator.clipboard?.writeText(inviteLink);
                            setLinkCopied(true);
                            setTimeout(() => setLinkCopied(false), 2000);
                          }}
                        >
                          {linkCopied ? '✓ Copied!' : '🔗 Copy Link'}
                        </button>
                      </div>
                    ) : (
                      <p style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.3)', textAlign: 'center', margin: '0.75rem 0 0' }}>Loading your invite link...</p>
                    )}

                    <div className="connection-cta-footer">
                      <p className="connection-cta-footer-text">Already shared? See who joined & run compatibility</p>
                      <button
                        className="connection-cta-go-btn"
                        onClick={onGoToConnections}
                      >
                        👥 Go to Connections
                      </button>
                    </div>
                  </div>
                </div>
              )}

            </div>
          )}

          {/* STAGE: ERROR */}
          {cardGenerationStage === 'error' && (
            <div className="text-center max-w-md mx-auto p-8 rounded-2xl shadow-xl border"
              style={{ background: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.12)' }}>
              <div className="text-4xl mb-4">🔄</div>
              <h3 className="text-xl font-bold text-white mb-2">AI took a different path</h3>
              <p className="text-sm mb-6" style={{ color: 'rgba(255,255,255,0.55)' }}>
                The AI sometimes needs a second attempt to structure your full profile. Hit retry — it usually works straight away.
              </p>
              <button
                onClick={handleGenerateCard}
                className="px-8 py-3 rounded-xl font-bold text-white transition-all"
                style={{ background: 'linear-gradient(135deg, #8b5cf6, #3b82f6)' }}
              >
                Retry Analysis
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};